import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "hebeh_assistant_session";

// Convierte URLs en enlaces clicables. Si es un PDF de cotización o ficha,
// muestra un chip visual "📎 Descargar PDF".
function renderMessageContent(text) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s)]+)/g;
  const parts = String(text).split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      const isPdf = /\.pdf(\?|$)/i.test(part) || /\/pdf(\?|$)/i.test(part) || /spec-sheets|quotations\/.+\/pdf/.test(part);
      if (isPdf) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-2 mr-2 px-3 py-1.5 rounded-full bg-[#F09A21]/20 border border-[#F09A21]/40 text-white text-xs hover:bg-[#F09A21]/30 transition-colors"
          >
            📎 Descargar PDF
          </a>
        );
      }
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-[#F09A21] hover:text-white break-all">
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// Widget flotante del Asistente de HEBEH.
// - Bottom-right, siempre visible en el catálogo automotriz.
// - Persiste session_id en localStorage para continuidad entre visitas.
// - Muestra typing indicator; los mensajes preservan saltos de línea.
export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Mensaje de bienvenida al abrir por primera vez
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "¡Hola! Soy el Asistente de HEBEH 🙂 ¿En qué te ayudo hoy?",
      }]);
    }
  }, [open, messages.length]);

  // Auto-scroll al último mensaje
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input al abrir
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chatbot/hebeh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || null,
          message: text,
        }),
      });
      const data = await res.json();
      if (data?.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch {}
      }
      const reply = data?.response || "Tuve un problema. ¿Puedes intentar de nuevo?";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "Tuve un problema de conexión. ¿Puedes intentar de nuevo?",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Cerrar chat" : "Abrir chat"}
        data-testid="assistant-fab"
        className="fixed bottom-6 right-5 z-50 w-12 h-12 rounded-full bg-[#F09A21] hover:bg-[#d68517] text-white shadow-[0_10px_30px_rgba(240,154,33,0.45)] flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div key="msg" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed bottom-24 right-4 md:right-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-8rem))] bg-[#0B1623] rounded-3xl shadow-2xl border border-white/10 flex flex-col overflow-hidden"
            data-testid="assistant-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-[#F09A21] to-[#E88A0F] flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-rajdhani font-semibold tracking-wide text-base leading-tight">Asistente de HEBEH</p>
                <p className="text-white/85 text-xs">Te ayudo a encontrar tu auto</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0B1623]" data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#F09A21] text-white rounded-br-md"
                        : m.role === "human_advisor"
                        ? "bg-blue-500/90 text-white rounded-bl-md"
                        : "bg-white/8 text-white/95 rounded-bl-md border border-white/10"
                    }`}
                    data-testid={`assistant-msg-${m.role}`}
                  >
                    {m.role === "human_advisor" && (
                      <div className="text-[10px] uppercase tracking-widest text-white/80 mb-1">Asesor HEBEH</div>
                    )}
                    {renderMessageContent(m.content)}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/8 border border-white/10 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#0B1623]">
              <div className="flex items-end gap-2 bg-white/5 rounded-2xl border border-white/10 focus-within:border-[#F09A21]/50 px-3 py-2 transition-colors">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Escribe tu mensaje..."
                  rows={1}
                  className="flex-1 resize-none bg-transparent outline-none text-white text-sm placeholder:text-white/40 max-h-24"
                  data-testid="assistant-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={!input.trim() || loading}
                  aria-label="Enviar"
                  data-testid="assistant-send-btn"
                  className="w-9 h-9 rounded-xl bg-[#F09A21] hover:bg-[#d68517] disabled:bg-white/10 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-white/30 text-[10px] mt-2 px-1">
                Powered by HEBEH · Tus datos se tratan conforme al <a href="https://privacidad.hebeh.com.mx/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/50">Aviso de Privacidad</a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
